/**
 * Simple easing functions for smooth transitions.
 * Used by templates and action handlers to interpolate values over time.
 */

export function easeInOut(t: number): number {
  return t < 0.5 ? 2 * t * t : -1 + (4 - 2 * t) * t;
}

export function easeOut(t: number): number {
  return 1 - Math.pow(1 - t, 3);
}

export function linear(t: number): number {
  return t;
}

/**
 * Interpolate between two numbers using an easing function.
 */
export function lerp(
  from: number,
  to: number,
  progress: number,
  easeFn: (t: number) => number = linear,
): number {
  const t = Math.max(0, Math.min(1, progress));
  return from + (to - from) * easeFn(t);
}

/* ─── Continuous motion toolkit (Anivaze v2) ──────────────────────────────
 * Every template receives `t` (seconds since the scene started) and animates as
 * a PURE FUNCTION of t — deterministic, so the live player and frame-accurate
 * export look identical. Never use Date.now()/Math.random() in render.
 * See design-plan/v2/ANIMATION-PATTERN.md.
 */

/** Sine oscillation between `min` and `max` with the given period (seconds). */
export function osc(t: number, periodSeconds: number, min = -1, max = 1, phase = 0): number {
  const s = (Math.sin(phase + (t / periodSeconds) * Math.PI * 2) + 1) / 2; // 0..1
  return min + (max - min) * s;
}

/** Angle (radians) for steady orbital/rotational motion: one revolution per period. */
export function orbitAngle(t: number, periodSeconds: number, phase = 0): number {
  return phase + (t / periodSeconds) * Math.PI * 2;
}

/** Small symmetric wobble around 0 (±amp). Good for floats, bobs, sway. */
export function wobble(t: number, periodSeconds: number, amp: number, phase = 0): number {
  return Math.sin(phase + (t / periodSeconds) * Math.PI * 2) * amp;
}

/** Draw-on / progress ramp: 0→1 over `durationSeconds`, eased, then holds at 1. */
export function drawOn(t: number, durationSeconds: number, delay = 0): number {
  return easeOut(Math.max(0, Math.min(1, (t - delay) / durationSeconds)));
}

/** Per-index phase offset for staggered/choreographed motion. */
export function stagger(index: number, stepSeconds: number): number {
  return index * stepSeconds;
}

/** Seamless loop phase in [0,1): fraction through the current `period` cycle. */
export function loop(t: number, periodSeconds: number): number {
  const p = (t % periodSeconds) / periodSeconds;
  return p < 0 ? p + 1 : p;
}

/** Clamp a value to [min,max]. */
export function clamp(value: number, min: number, max: number): number {
  return value < min ? min : value > max ? max : value;
}

/** Sharp recurring spike in [0,1] (e.g. a heartbeat / action-potential), one per period. */
export function pulse(t: number, periodSeconds: number, phase = 0): number {
  return Math.max(0, Math.sin(phase + (t / periodSeconds) * Math.PI * 2));
}

/**
 * Position along a polyline of points at progress `p` in [0,1].
 * Great for particles travelling a multi-segment path (wires, pipes, circuits).
 */
export function pathLerp(points: Array<{ x: number; y: number }>, p: number): { x: number; y: number } {
  if (points.length === 0) return { x: 0, y: 0 };
  if (points.length === 1) return points[0];
  const clamped = p < 0 ? 0 : p > 1 ? 1 : p;
  const seg = clamped * (points.length - 1);
  const i = Math.min(Math.floor(seg), points.length - 2);
  const f = seg - i;
  return { x: lerp(points[i].x, points[i + 1].x, f), y: lerp(points[i].y, points[i + 1].y, f) };
}
